import { MessageSquare, Send } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import TradeMessageItem from "@/components/p2p/TradeMessageItem";

import type { Trade } from "@/interfaces/trade";
import type { TradeMessage } from "@/interfaces/message/message";
import type { Dispute } from "@/interfaces/dispute";

type CommunicationStatus = {
  label: string;
  className: string;
};

type TradeCommunicationDialogProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      tradeId: string;
      messages: TradeMessage[];
      trade: Trade;
      dispute?: Dispute;
      status?: CommunicationStatus;
      readOnly: true;
    }
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      tradeId: string;
      messages: TradeMessage[];
      trade: Trade;
      dispute?: Dispute;
      status?: CommunicationStatus;
      message: string;
      onMessageChange: (value: string) => void;
      onSendMessage: () => void;
      isSending?: boolean;
      readOnly?: false;
    };

const TradeCommunicationDialog = (props: TradeCommunicationDialogProps) => {
  const { open, onOpenChange, tradeId, messages, trade, dispute, status } =
    props;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] w-[95vw] max-w-4xl flex-col overflow-hidden rounded-2xl border border-border/60 bg-background p-0 shadow-2xl">
        {/* Header */}
        <div className="shrink-0 border-b border-border/60 bg-background/95 backdrop-blur">
          <div className="px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                    <MessageSquare className="h-5 w-5 text-primary" />
                  </div>

                  <div>
                    <DialogTitle className="text-lg font-semibold tracking-tight">
                      Trade Communication
                    </DialogTitle>

                    <DialogDescription className="mt-0.5 text-xs">
                      Moderator communication with the buyer and seller
                    </DialogDescription>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="font-mono text-[11px]">
                    Trade #{tradeId.slice(0, 8)}
                  </Badge>

                  <Badge variant="secondary" className="text-[11px]">
                    {messages.length}{" "}
                    {messages.length === 1 ? "message" : "messages"}
                  </Badge>

                  {status && (
                    <Badge variant="outline" className={status.className}>
                      {status.label}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conversation */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/20">
          <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
            {messages.length === 0 ? (
              <div className="flex min-h-[400px] items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <MessageSquare className="h-5 w-5 text-muted-foreground" />
                  </div>

                  <p className="text-sm font-medium text-foreground">
                    No messages yet
                  </p>

                  <p className="mt-1 text-xs text-muted-foreground">
                    No communication has been recorded for this trade.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg) => (
                  <TradeMessageItem
                    key={msg.id}
                    msg={msg}
                    dispute={dispute}
                    trade={trade}
                    moderatorView
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        {!props.readOnly && (
          <div className="shrink-0 border-t border-border/60 bg-background">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                props.onSendMessage();
              }}
              className="mx-auto w-full max-w-3xl px-4 py-4 sm:px-6"
            >
              <div className="rounded-2xl border border-border/70 bg-muted/20 p-2 shadow-sm">
                <div className="flex items-end gap-2">
                  <textarea
                    value={props.message}
                    onChange={(e) => props.onMessageChange(e.target.value)}
                    placeholder="Send a message to the buyer and seller..."
                    rows={2}
                    className="min-h-[52px] flex-1 resize-none border-0 bg-transparent px-3 py-2 text-sm outline-none ring-0 placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                    disabled={props.isSending}
                  />

                  <Button
                    type="submit"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl"
                    disabled={!props.message.trim() || props.isSending}
                  >
                    {props.isSending ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                <div className="flex items-center justify-between px-3 pb-1 pt-1">
                  <p className="text-[11px] text-muted-foreground">
                    Your message will be visible to both traders.
                  </p>

                  <span className="text-[11px] text-muted-foreground">
                    Moderator
                  </span>
                </div>
              </div>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TradeCommunicationDialog;
